import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { useAlert } from '../../context/AlertContext';
import { useConfirm } from '../../context/ConfirmContext';
import { API_ENDPOINTS } from '../../config/api';
import {
    Search,
    Mail,
    CheckCircle,
    XCircle,
    UserCheck,
    UserX,
    RefreshCw
} from 'lucide-react';

const VerifyEmail = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('All');
    const { showAlert } = useAlert();
    const { showConfirm } = useConfirm();

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(API_ENDPOINTS.ADMIN_MANAGE_EMAIL_VERIFICATION, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setUsers(data.data);
            } else {
                showAlert(data.message || 'Failed to fetch users', 'error');
            }
        } catch (err) {
            console.error('Error fetching users:', err);
            showAlert('Failed to connect to server', 'error');
        } finally {
            setLoading(false);
        }
    }, [showAlert]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleToggleVerification = async (user, status) => {
        const actionText = status ? 'Verify' : 'Unverify';
        const confirmed = await showConfirm(
            `${actionText} Email`,
            `Are you sure you want to ${actionText.toLowerCase()} email for ${user.name}?`,
            status ? 'success' : 'danger'
        );

        if (!confirmed) return;

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(API_ENDPOINTS.ADMIN_MANAGE_EMAIL_VERIFICATION, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ id: user.id, status: status })
            });
            const data = await response.json();
            if (data.success) {
                showAlert(data.message, 'success');
                fetchUsers();
            } else {
                showAlert(data.message || 'Failed to update status', 'error');
            }
        } catch (err) {
            console.error('Error updating status:', err);
            showAlert('Failed to connect to server', 'error');
        }
    };

    const filteredUsers = users.filter(user => {
        const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.user_id.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesFilter = filterStatus === 'All' ||
            (filterStatus === 'Verified' && user.isVerified) ||
            (filterStatus === 'Unverified' && !user.isVerified);

        return matchesSearch && matchesFilter;
    });

    const verifiedCount = users.filter(u => u.isVerified).length;
    const unverifiedCount = users.filter(u => !u.isVerified).length;

    return (
        <Layout>
            <div className="space-y-6">
                {/* Page Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Verify User Email</h1>
                        <p className="text-gray-500 text-sm mt-1">Manage email verification status for all users</p>
                    </div>
                    <button
                        onClick={fetchUsers}
                        disabled={loading}
                        className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium flex items-center gap-2 shadow-sm transition-all disabled:opacity-50"
                    >
                        <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                        Refresh List
                    </button>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                        <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider">Total Users</p>
                        <h3 className="text-2xl font-bold text-gray-900 mt-1">{users.length}</h3>
                        <div className="p-2 bg-blue-50 text-blue-600 rounded-lg absolute top-5 right-5">
                            <Mail size={20} />
                        </div>
                    </div>
                    <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 relative">
                        <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider">Verified</p>
                        <h3 className="text-2xl font-bold text-green-600 mt-1">{verifiedCount}</h3>
                        <div className="p-2 bg-green-50 text-green-600 rounded-lg absolute top-5 right-5">
                            <CheckCircle size={20} />
                        </div>
                    </div>
                    <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 relative">
                        <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider">Unverified</p>
                        <h3 className="text-2xl font-bold text-red-600 mt-1">{unverifiedCount}</h3>
                        <div className="p-2 bg-red-50 text-red-600 rounded-lg absolute top-5 right-5">
                            <XCircle size={20} />
                        </div>
                    </div>
                </div>

                {/* Filters & Search */}
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row justify-between gap-4 items-center">
                    <div className="relative w-full md:w-96">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="text"
                            placeholder="Search by name, email or ID..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="flex gap-2 w-full md:w-auto">
                        <select
                            className="px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-700"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <option value="All">All Status</option>
                            <option value="Verified">Verified Only</option>
                            <option value="Unverified">Unverified Only</option>
                        </select>
                    </div>
                </div>

                {/* Users Table */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-100 text-gray-500 text-xs uppercase tracking-wider">
                                    <th className="px-6 py-4 font-semibold">User</th>
                                    <th className="px-6 py-4 font-semibold">User ID</th>
                                    <th className="px-6 py-4 font-semibold">Join Date</th>
                                    <th className="px-6 py-4 font-semibold">Email Status</th>
                                    <th className="px-6 py-4 font-semibold text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {loading ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-10 text-center text-gray-500">
                                            <div className="flex flex-col items-center gap-2">
                                                <RefreshCw size={24} className="animate-spin text-blue-600" />
                                                <p>Loading users...</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-10 text-center text-gray-500">
                                            <p>No users found matching your criteria.</p>
                                        </td>
                                    </tr>
                                ) : filteredUsers.map((user) => (
                                    <tr key={user.id} className="hover:bg-gray-50/50 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center text-blue-700 font-bold text-sm">
                                                    {user.avatar}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{user.name}</p>
                                                    <p className="text-xs text-gray-500">{user.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-700 font-medium">
                                            {user.user_id}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            {user.joinDate}
                                        </td>
                                        <td className="px-6 py-4">
                                            {user.isVerified ? (
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200">
                                                    <CheckCircle size={12} /> Verified
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-200">
                                                    <XCircle size={12} /> Unverified
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            {user.isVerified ? (
                                                <button
                                                    onClick={() => handleToggleVerification(user, false)}
                                                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-xs font-bold"
                                                    title="Mark as Unverified"
                                                >
                                                    <UserX size={14} /> Mark Unverified
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => handleToggleVerification(user, true)}
                                                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-xs font-bold"
                                                    title="Verify Email"
                                                >
                                                    <UserCheck size={14} /> Verify Now
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {!loading && filteredUsers.length > 0 && (
                        <div className="p-4 border-t border-gray-100 flex justify-between items-center text-sm text-gray-500">
                            <span>Showing {filteredUsers.length} of {users.length} users</span>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default VerifyEmail;
